import {
  clampMinutes,
  formatMinutesAsTimeInput,
  parseTimeToMinutes,
  TIMELINE_DAY_END_MINUTES,
  TIMELINE_MIN_CLASS_DURATION_MINUTES,
} from "utils/scheduleTimelineUtils";
import { normalizeAutoClassChange } from "utils/autoClassChangeConfig";

export const MAX_SCHEDULE_NAME_LENGTH = 20;

export const SCHEDULE_PASTEL_COLORS = [
  "#D32F2F", "#E64A19", "#F57C00", "#FF9800", "#FBC02D", "#FDD835",
  "#C0CA33", "#8BC34A", "#4CAF50", "#009688", "#00ACC1", "#03A9F4",
  "#1E88E5", "#3949AB", "#5E35B1", "#8E24AA", "#AB47BC", "#D81B60",
  "#E91E63", "#F06292", "#8D6E63", "#78909C", "#546E7A", "#455A64",
];

// Distinct, classroom-friendly hues for automatic schedule pattern assignment.
// Ordered so the first few generated patterns are easy to tell apart.
export const SCHEDULE_AUTO_ASSIGN_COLORS = [
  "#1E88E5",
  "#43A047",
  "#8E24AA",
  "#00897B",
  "#F57C00",
  "#5E35B1",
  "#00ACC1",
  "#D81B60",
  "#7CB342",
  "#3949AB",
  "#FB8C00",
  "#C2185B",
  "#546E7A",
  "#6D4C41",
];

export const DEFAULT_SCHEDULE_COLOR = "#03A9F4";

export function getScheduleAutoAssignColor(index = 0) {
  if (!Number.isFinite(index) || index < 0) {
    return SCHEDULE_AUTO_ASSIGN_COLORS[0];
  }

  return SCHEDULE_AUTO_ASSIGN_COLORS[index % SCHEDULE_AUTO_ASSIGN_COLORS.length];
}

export const normalizeScheduleColor = (value) => String(value || "").trim().toLowerCase();

export function getScheduleColorUsageByColor(schedules = [], excludeScheduleId = "") {
  const excludeId = excludeScheduleId ? String(excludeScheduleId) : "";
  const usageMap = {};

  (Array.isArray(schedules) ? schedules : []).forEach((schedule) => {
    if (!schedule?.id || !schedule?.color) return;
    if (excludeId && String(schedule.id) === excludeId) return;

    const color = normalizeScheduleColor(schedule.color);
    if (!color || usageMap[color]) return;

    usageMap[color] = String(schedule.label || "").trim() || "Schedule";
  });

  return usageMap;
}

export function getUsedScheduleColors(schedules = [], excludeScheduleId = "") {
  return new Set(Object.keys(getScheduleColorUsageByColor(schedules, excludeScheduleId)));
}

const getScheduleOrderValue = (schedule) => {
  const parsedOrder = Number(schedule?.order);
  return Number.isFinite(parsedOrder) ? parsedOrder : null;
};

export function sortSchedulesByOrder(schedules = []) {
  return (Array.isArray(schedules) ? schedules : [])
    .map((schedule, index) => ({ schedule, index }))
    .sort((left, right) => {
      const leftOrder = getScheduleOrderValue(left.schedule);
      const rightOrder = getScheduleOrderValue(right.schedule);

      if (leftOrder !== null && rightOrder !== null && leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      if (leftOrder !== null && rightOrder === null) return -1;
      if (leftOrder === null && rightOrder !== null) return 1;

      if (leftOrder === null && rightOrder === null) {
        const labelDifference = String(left.schedule?.label || "").localeCompare(
          String(right.schedule?.label || ""),
          undefined,
          { numeric: true, sensitivity: "base" }
        );
        if (labelDifference !== 0) return labelDifference;
      }

      return left.index - right.index;
    })
    .map(({ schedule }) => schedule);
}

export function normalizeSchedulesWithOrder(schedules = []) {
  return sortSchedulesByOrder(schedules).map((schedule, index) => ({
    ...schedule,
    order: index,
  }));
}

export function reorderSchedulesInOrder(schedules = [], sourceIndex = -1, destinationIndex = -1) {
  const orderedSchedules = normalizeSchedulesWithOrder(schedules);
  const fromIndex = Number(sourceIndex);
  const toIndex = Number(destinationIndex);

  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= orderedSchedules.length ||
    toIndex >= orderedSchedules.length ||
    fromIndex === toIndex
  ) {
    return orderedSchedules;
  }

  const nextSchedules = [...orderedSchedules];
  const [movedSchedule] = nextSchedules.splice(fromIndex, 1);
  nextSchedules.splice(toIndex, 0, movedSchedule);

  return nextSchedules.map((schedule, index) => ({
    ...schedule,
    order: index,
  }));
}

export function moveScheduleInOrder(schedules = [], scheduleId = "", direction = 0) {
  const orderedSchedules = normalizeSchedulesWithOrder(schedules);
  const currentIndex = orderedSchedules.findIndex(
    (schedule) => String(schedule?.id || "") === String(scheduleId || "")
  );

  if (currentIndex < 0) return orderedSchedules;

  return reorderSchedulesInOrder(
    orderedSchedules,
    currentIndex,
    currentIndex + Number(direction)
  );
}

export function sanitizeScheduleClassesToKnownClasses(classes = [], knownClassIds = []) {
  const knownClassIdSet = new Set(
    (Array.isArray(knownClassIds) ? knownClassIds : [])
      .filter((classId) => classId !== null && classId !== undefined)
      .map((classId) => String(classId))
  );

  return (Array.isArray(classes) ? classes : []).filter(
    (block) => block?.classId && knownClassIdSet.has(String(block.classId))
  );
}

export function scheduleCalendarReferencesClass(scheduleCalendar = {}, classId = "") {
  const normalizedClassId = String(classId || "");
  if (!normalizedClassId) return false;

  const templates = Array.isArray(scheduleCalendar?.templates) ? scheduleCalendar.templates : [];
  return templates.some((template) => {
    const templateClasses = Array.isArray(template?.classes) ? template.classes : [];
    return templateClasses.some((block) => String(block?.classId || "") === normalizedClassId);
  });
}

const DEFAULT_DRAFT_START_TIME = "08:00";
const DEFAULT_DRAFT_PERIOD_MINUTES = 50;
const DEFAULT_PASSING_PERIOD_MINUTES = 5;
const MAX_INFERRED_PASSING_MINUTES = 30;

const getValidScheduleBlocks = (classes = []) =>
  (Array.isArray(classes) ? classes : [])
    .map((block) => ({
      startMinutes: parseTimeToMinutes(block?.startTime),
      endMinutes: parseTimeToMinutes(block?.endTime),
    }))
    .filter(
      (block) =>
        block.startMinutes !== null &&
        block.endMinutes !== null &&
        block.endMinutes > block.startMinutes
    )
    .sort((leftBlock, rightBlock) => leftBlock.startMinutes - rightBlock.startMinutes);

const pickMostCommonValue = (values = [], tieBreakerValue = null) => {
  if (!values.length) return null;

  const counts = new Map();
  values.forEach((value) => {
    counts.set(value, (counts.get(value) || 0) + 1);
  });

  const maxCount = Math.max(...counts.values());
  const candidates = [...counts.entries()]
    .filter(([, count]) => count === maxCount)
    .map(([value]) => value);

  if (candidates.length === 1) {
    return candidates[0];
  }

  if (tieBreakerValue !== null && candidates.includes(tieBreakerValue)) {
    return tieBreakerValue;
  }

  return candidates.sort((leftValue, rightValue) => rightValue - leftValue)[0];
};

const inferPeriodDurationMinutes = (blocks = [], fallbackDurations = []) => {
  const durations = blocks.map((block) => block.endMinutes - block.startMinutes);
  if (durations.length) {
    return pickMostCommonValue(durations, durations[durations.length - 1]);
  }

  if (fallbackDurations.length) {
    return pickMostCommonValue(
      fallbackDurations,
      fallbackDurations[fallbackDurations.length - 1]
    );
  }

  return DEFAULT_DRAFT_PERIOD_MINUTES;
};

const collectPassingGapsFromBlocks = (blocks = []) => {
  const gaps = [];
  for (let index = 1; index < blocks.length; index += 1) {
    const gap = blocks[index].startMinutes - blocks[index - 1].endMinutes;
    if (gap >= 0 && gap <= MAX_INFERRED_PASSING_MINUTES) {
      gaps.push(gap);
    }
  }
  return gaps;
};

const pickPassingPeriodFromGaps = (gaps = []) => {
  if (!gaps.length) return null;
  return pickMostCommonValue(gaps, gaps[gaps.length - 1]);
};

const collectTimingSignalsFromSchedules = (schedules = [], excludeScheduleId = "") => {
  const excludeId = String(excludeScheduleId || "");
  const durations = [];
  const gaps = [];

  (Array.isArray(schedules) ? schedules : []).forEach((schedule) => {
    if (excludeId && String(schedule?.id) === excludeId) return;

    const blocks = getValidScheduleBlocks(schedule?.classes);
    blocks.forEach((block) => {
      durations.push(block.endMinutes - block.startMinutes);
    });
    gaps.push(...collectPassingGapsFromBlocks(blocks));
  });

  return { durations, gaps };
};

const inferPassingPeriodMinutes = (blocks = [], options = {}) => {
  const currentPassing = pickPassingPeriodFromGaps(collectPassingGapsFromBlocks(blocks));
  if (currentPassing !== null) {
    return currentPassing;
  }

  const fallbackPassing = pickPassingPeriodFromGaps(options.fallbackGaps);
  if (fallbackPassing !== null) {
    return fallbackPassing;
  }

  if (blocks.length === 1) {
    const autoClassChangeOffsetMinutes = Number(options.autoClassChangeOffsetMinutes);
    if (Number.isFinite(autoClassChangeOffsetMinutes) && autoClassChangeOffsetMinutes > 0) {
      return autoClassChangeOffsetMinutes;
    }
  }

  return DEFAULT_PASSING_PERIOD_MINUTES;
};

export function getAutoClassChangeOffsetMinutesForClass(appClasses = [], classId = "") {
  const classItem = (Array.isArray(appClasses) ? appClasses : []).find(
    (item) => String(item?.classId) === String(classId)
  );
  if (!classItem) return null;

  const normalized = normalizeAutoClassChange(classItem.autoClassChange);
  if (!normalized.active || normalized.offsetMinutesBeforeStart <= 0) {
    return null;
  }

  return normalized.offsetMinutesBeforeStart;
};

export function getDefaultDraftTimesForSchedule(classes = [], options = {}) {
  const blocks = getValidScheduleBlocks(classes);
  const { durations: fallbackDurations, gaps: fallbackGaps } = collectTimingSignalsFromSchedules(
    options.otherSchedules,
    options.scheduleId ?? options.excludeScheduleId
  );

  if (!blocks.length) {
    const periodDurationMinutes = inferPeriodDurationMinutes([], fallbackDurations);
    const startMinutes = parseTimeToMinutes(DEFAULT_DRAFT_START_TIME) ?? 8 * 60;
    const endMinutes = clampMinutes(
      startMinutes + periodDurationMinutes,
      startMinutes + TIMELINE_MIN_CLASS_DURATION_MINUTES,
      TIMELINE_DAY_END_MINUTES
    );

    return {
      startTime: DEFAULT_DRAFT_START_TIME,
      endTime: formatMinutesAsTimeInput(endMinutes),
    };
  }

  const periodDurationMinutes = inferPeriodDurationMinutes(blocks, fallbackDurations);
  const passingPeriodMinutes = inferPassingPeriodMinutes(blocks, {
    ...options,
    fallbackGaps,
  });
  const lastBlock = blocks[blocks.length - 1];
  const nextStartMinutes = clampMinutes(lastBlock.endMinutes + passingPeriodMinutes);
  const nextEndMinutes = clampMinutes(
    nextStartMinutes + periodDurationMinutes,
    nextStartMinutes + TIMELINE_MIN_CLASS_DURATION_MINUTES,
    TIMELINE_DAY_END_MINUTES
  );

  return {
    startTime: formatMinutesAsTimeInput(nextStartMinutes),
    endTime: formatMinutesAsTimeInput(nextEndMinutes),
  };
}

export function getDefaultDraftTimesForClass(classes = [], classId = "", options = {}) {
  const autoClassChangeOffsetMinutes =
    options.autoClassChangeOffsetMinutes ??
    getAutoClassChangeOffsetMinutesForClass(options.appClasses, classId);

  return getDefaultDraftTimesForSchedule(classes, {
    ...options,
    autoClassChangeOffsetMinutes,
    scheduleId: options.scheduleId ?? options.excludeScheduleId,
  });
}

export function removeClassFromScheduleCalendar(scheduleCalendar = {}, classIdToRemove = "") {
  const normalizedClassId = String(classIdToRemove || "");
  if (!normalizedClassId) {
    return { scheduleCalendar, changed: false };
  }

  const templates = Array.isArray(scheduleCalendar?.templates) ? scheduleCalendar.templates : [];
  let changed = false;

  const nextTemplates = templates.map((template) => {
    const templateClasses = Array.isArray(template?.classes) ? template.classes : [];
    const nextClasses = templateClasses.filter(
      (block) => String(block?.classId || "") !== normalizedClassId
    );

    if (nextClasses.length !== templateClasses.length) {
      changed = true;
      return { ...template, classes: nextClasses };
    }

    return template;
  });

  if (!changed) {
    return { scheduleCalendar, changed: false };
  }

  return {
    scheduleCalendar: {
      ...scheduleCalendar,
      templates: nextTemplates,
    },
    changed: true,
  };
}
